import { consumer, core } from '@tock/coretypes';
import { isEmpty } from 'es-toolkit/compat';
import { offeringSingleSlugConsumer } from './singleSlugConsumer';

function sanitizeSlug(slug?: string) {
  if (slug === 'menu') {
    return 'experience-menu';
  }
  return slug;
}

/**
 * Returns the public relative link to the detail page of this type of offering on
 * consumer, for the id given, not including the business domain
 * e.g. /event/1234/some-event-slug, /special-table/1234/some-special-table-slug
 */
export function offeringConsumerDetailPagePublicRelativeLink(
  variety: core.TicketType.Variety | consumer.Offering.Type,
  id: number,
  slug?: string,
  businessDomain?: string,
  detailsPageMode?: DetailsPageMode
) {
  const businessPrefix =
    (businessDomain != null) && !isEmpty(businessDomain) ? `/${businessDomain}` : '';
  const slugSuffix = sanitizeSlug(slug) ? `/${sanitizeSlug(slug)}` : '';
  return `${businessPrefix}/${detailPageSlug(variety, detailsPageMode)}/${id}${slugSuffix}`;
}
/**
 * Returns the private relative link to the detail page of this type of offering on
 * consumer, for the id given, not including the business domain
 * e.g. /event/1234/some-event-slug, /special-table/1234/some-special-table-slug
 */
export function offeringConsumerDetailPagePrivateRelativeLink(
  variety: core.TicketType.Variety | consumer.Offering.Type,
  privateAccessToken: string,
  businessDomain?: string,
  detailsPageMode?: DetailsPageMode
) {
  const businessPrefix =
    (businessDomain != null) && !isEmpty(businessDomain) ? `/${businessDomain}` : '';
  return `${businessPrefix}/${detailPageSlug(
    variety,
    detailsPageMode
  )}/private/${privateAccessToken}`;
}

/**
 * Returns the slug to use for an offering's detail page. Detail pages are special because
 * they break the URL pattern: For events, all urls use "event" as the slug including the
 * detail page. However for experiences, there are two different notions of a "detail"
 * page: one that pops up an a modal to select a date, and one that shows the experience's
 * detail page.
 */
function detailPageSlug(
  variety: core.TicketType.Variety | consumer.Offering.Type,
  detailsPageMode?: DetailsPageMode
): string {
  const singleSlug = offeringSingleSlugConsumer(variety);
  return singleSlug === 'experience' && detailsPageMode === 'showDetails' ? 'detail' : singleSlug;
}

/**
 * Returns the public relative link to the detail page of this type of offering on
 * consumer, for the id given, not including the business domain
 * e.g. /event/1234/menu/some-event-slug, /special-table/1234/menu/some-special-table-slug
 */
export function offeringConsumerMenuPagePublicRelativeLink(
  variety: core.TicketType.Variety | consumer.Offering.Type,
  id: number,
  slug?: string,
  businessDomain?: string
) {
  const businessPrefix =
    (businessDomain != null) && !isEmpty(businessDomain) ? `/${businessDomain}` : '';
  const slugSuffix = sanitizeSlug(slug) ? `/${sanitizeSlug(slug)}` : '';
  return `${businessPrefix}/${offeringSingleSlugConsumer(variety)}/${id}/menu${slugSuffix}`;
}
/**
 * Returns the private relative link to the detail page of this type of offering on
 * consumer, for the id given, not including the business domain
 * e.g. /event/1234/menu/some-event-slug, /special-table/1234/menu/some-special-table-slug
 */
export function offeringConsumerMenuPagePrivateRelativeLink(
  variety: core.TicketType.Variety | consumer.Offering.Type,
  privateAccessToken: string,
  businessDomain?: string
) {
  const businessPrefix =
    (businessDomain != null) && !isEmpty(businessDomain) ? `/${businessDomain}` : '';
  return `${businessPrefix}/${offeringSingleSlugConsumer(
    variety
  )}/private/menu/${privateAccessToken}`;
}

export type DetailsPageMode = 'showBooking' | 'showDetails';

/**
 * Returns a link to the given experience's page relative to the business's
 * page. For instance it might return /event/1234, which would need to be
 * prepended with https://www.exploretock.com/my-business to be a full URL.
 * If the passed
 */
export function offeringConsumerDetailPageRelativeLink(
  offering: core.TicketType | consumer.Offering,
  businessDomain?: string,
  detailsPageMode?: DetailsPageMode
) {
  if ('hasPrivateUrl' in offering && offering.hasPrivateUrl) {
    return offeringConsumerDetailPagePrivateRelativeLink(
      offering.variety,
      offering.privateAccessToken,
      businessDomain,
      detailsPageMode
    );
  }
  return offeringConsumerDetailPagePublicRelativeLink(
    'variety' in offering ? offering.variety : offering.type,
    offering.id,
    offering.slug,
    businessDomain,
    detailsPageMode
  );
}

/**
 * Returns a link to the given experience's page relative to the business's
 * page. For instance it might return /event/1234, which would need to be
 * prepended with https://www.exploretock.com/my-business to be a full URL.
 * If the passed
 */
export function offeringConsumerMenuPageRelativeLink(
  offering: core.TicketType | consumer.Offering,
  businessDomain?: string
) {
  if ('hasPrivateUrl' in offering && offering.hasPrivateUrl) {
    return offeringConsumerMenuPagePrivateRelativeLink(
      offering.variety,
      offering.privateAccessToken,
      businessDomain
    );
  }
  return offeringConsumerMenuPagePublicRelativeLink(
    'variety' in offering ? offering.variety : offering.type,
    offering.id,
    offering.slug,
    businessDomain
  );
}
